import moment from 'moment'

// Helpers shared by the activity Varaukset tab and the reverse picker in
// ReservationModal. Kept pure so the awkward parts -- three time formats and the
// dash-joined collision string -- are testable without a browser.

export const RESERVATION_TIMEFORMAT = 'YYYY-MM-DDTHH:mm:ssZ'
export const ACTIVITY_TIMEFORMAT = 'YYYY-MM-DDTHH:mm'

// redis-isconflict returns `collision` as "<startUnix>-<endUnix>-<title>". The title
// may itself contain dashes, so only the first two separators are structural.
export const parseCollision = (collision) => {
  if (!collision || typeof collision !== 'string') return null
  const parts = collision.split('-')
  if (parts.length < 2) return null
  const start = parseInt(parts[0], 10)
  const end = parseInt(parts[1], 10)
  if (!isFinite(start) || !isFinite(end)) return null
  return { start, end, title: parts.slice(2).join('-') }
}

export const formatCollision = (collision) => {
  const parsed = parseCollision(collision)
  if (!parsed) return ''
  const start = moment.unix(parsed.start).utc()
  const end = moment.unix(parsed.end).utc()
  const when = start.format('DD.MM.YY HH:mm') + '-' + end.format('HH:mm')
  return parsed.title ? when + ' ' + parsed.title : when
}

// The live redis-isconflict payload carries no reservation id, so a reservation being
// moved cannot be excluded from its own conflict results by id. Drop collisions whose
// time range matches where the reservation currently sits instead. Heuristic, but it
// is the only signal the callable gives us.
export const dropSelfCollisions = (conflictRow, previousSlots) => {
  if (!conflictRow || !conflictRow.isconflict) return conflictRow
  const parsed = parseCollision(conflictRow.collision)
  if (!parsed) return conflictRow
  const isSelf = (previousSlots || []).some((slot) => {
    return (
      moment.utc(slot.start).unix() === parsed.start && moment.utc(slot.end).unix() === parsed.end
    )
  })
  return isSelf ? { ...conflictRow, isconflict: false, selfCollision: true } : conflictRow
}

// Normalises any of the time strings floating around reservations into
// RESERVATION_TIMEFORMAT. Passes an unreadable value through untouched: moment.utc(
// undefined) is the CURRENT time, which would silently invent a booking time.
export const toReservationTime = (value) => {
  if (!value) return value
  const parsed = moment.utc(value)
  return parsed.isValid() ? parsed.format(RESERVATION_TIMEFORMAT) : value
}

// True when an attached reservation no longer sits at the activity's time.
//
// Compares INSTANTS, not strings. The same moment reaches this function spelled several
// ways depending on who wrote it: reservations this feature creates go through
// buildActivityReservation and come out as '...T18:00:00+00:00', while a reservation
// merely attached carries whatever the booking index holds, which is Zulu with
// milliseconds ('...T18:00:00.000Z' - see the parse format at CourtsContainer.jsx:513).
// A string comparison called those two different and reported drift on every save of an
// activity whose booking was attached rather than created, no matter what was edited -
// and the dialog then showed two identical times, because it renders both through
// moment.utc.
//
// An unparseable time counts as NOT drifted. Claiming drift is the destructive
// direction: it offers to move a court booking that is in fact sitting correctly.
export const hasDrifted = (activity, reservation) => {
  if (!activity || !reservation) return false
  if (!activity.start || !activity.end) return false
  if (!reservation.start || !reservation.end) return false
  const activityStart = moment.utc(activity.start)
  const activityEnd = moment.utc(activity.end)
  const reservationStart = moment.utc(reservation.start)
  const reservationEnd = moment.utc(reservation.end)
  if (!activityStart.isValid() || !activityEnd.isValid()) return false
  if (!reservationStart.isValid() || !reservationEnd.isValid()) return false
  return !activityStart.isSame(reservationStart) || !activityEnd.isSame(reservationEnd)
}

export const driftedReservations = (activity, reservations) =>
  (reservations || []).filter((r) => hasDrifted(activity, r))

// Only reservations this feature created are ours to release; ones merely linked to
// the activity belong to whoever booked them and are unlinked, never cancelled.
export const createdReservations = (reservations) =>
  (reservations || []).filter((r) => r.attached === 'created')

export const formatReservationTime = (reservation) => {
  if (!reservation || !reservation.start) return ''
  const start = moment.utc(reservation.start)
  const end = moment.utc(reservation.end)
  return start.format('dd D.M.YYYY HH:mm') + '-' + end.format('HH:mm')
}

// The payer a new activity booking should default to: the activity's first coach.
export const defaultPayersForActivity = (activity) => {
  if (activity && activity.organisers && activity.organisers.length > 0) {
    return [activity.organisers[0]]
  }
  return []
}

// The summary stored on activity.reservations. Both attach paths -- the Varaukset tab
// and the picker inside ReservationModal -- go through this, so the shape cannot drift
// apart between them.
//
// Times are normalised on the way in. Both attach paths hand over whatever the booking
// index gave them, which is not the format createActivityReservations writes, so without
// this the stored summaries come in two spellings of the same instant. hasDrifted no
// longer cares, but keeping one spelling on disk means the two halves of
// activity.reservations stay comparable by eye and in the console.
export const buildReservationSummary = (entry, resource, attached) => ({
  id: entry.id,
  resourceid: entry.resourceId || entry.resourceid || (resource ? resource.id : ''),
  resourcename: resource ? resource.displayName || '' : entry.resourcename || '',
  start: toReservationTime(entry.start),
  end: toReservationTime(entry.end),
  cost: entry.paymentstatus ? entry.paymentstatus.cost / 100 : Number(entry.cost) || 0,
  attached: attached || 'existing',
})

export const isAttached = (reservations, reservationid) =>
  (reservations || []).some((r) => r.id === reservationid)
